import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Minus, Plus, Search, Send, LogOut, Maximize2, Loader2, BellRing, Check, ClipboardList } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { useLive } from "../lib/live";
import "./captain.css";

const STATUS_FILTERS = [["all", "All"], ["free", "Free"], ["occupied", "Occupied"]];
const ITEM_LABEL = { pending: "Not sent", sent: "In kitchen", new: "In kitchen", ready: "Ready", served: "Served", cancelled: "Cancelled" };

function TablePicker({ tables, onPick, busy }) {
  const [st, setSt] = useState("all");
  const [sec, setSec] = useState("");
  const sections = [...new Set(tables.map((t) => t.section || "Hall"))];
  const shown = tables.filter((t) => (st === "all" || (st === "free" ? t.status !== "occupied" : t.status === "occupied")) && (!sec || (t.section || "Hall") === sec));
  const count = (k) => tables.filter((t) => k === "all" || (k === "free" ? t.status !== "occupied" : t.status === "occupied")).length;
  return (
    <>
      <div className="cap-cats" data-testid="cap-status-filter">{STATUS_FILTERS.map(([k, l]) => <button key={k} className={st === k ? "on" : ""} onClick={() => setSt(k)} data-testid={`cap-filter-${k}`}>{l} · {count(k)}</button>)}</div>
      {sections.length > 1 && <div className="cap-cats" data-testid="cap-section-filter"><button className={!sec ? "on" : ""} onClick={() => setSec("")} data-testid="cap-section-all">All sections</button>{sections.map((s) => <button key={s} className={sec === s ? "on" : ""} onClick={() => setSec(s)} data-testid={`cap-section-${s}`}>{s}</button>)}</div>}
      {sections.filter((s) => !sec || s === sec).map((s) => {
        const list = shown.filter((t) => (t.section || "Hall") === s);
        return list.length ? (
          <section key={s} className="cap-section">
            <h3>{s}</h3>
            <div className="cap-tables">
              {list.map((t) => (
                <button key={t.id} disabled={busy} className={`cap-table ${t.status === "occupied" ? "busy" : ""} ${t.tab_summary?.ready_to_bill ? "ready" : ""}`} onClick={() => onPick(t)} data-testid={`cap-table-${t.id}`}>
                  <b>{t.name || t.number}</b><small>{t.status === "occupied" ? `${t.tab_summary?.items || 0} item${(t.tab_summary?.items || 0) === 1 ? "" : "s"} · ${money(t.tab_summary?.amount || 0)}` : "Free"}</small>
                </button>
              ))}
            </div>
          </section>
        ) : null;
      })}
      {!shown.length && <p className="cap-empty-note" data-testid="cap-no-tables">No tables match this filter.</p>}
    </>
  );
}

function OrderView({ tabId, onAdd }) {
  const [tab, setTab] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/tabs/${tabId}`).then(({ data }) => setTab(data)).catch((e) => setErr(errorMessage(e)));
  useEffect(() => { load(); }, [tabId]); // eslint-disable-line
  useLive(() => load(), ["tabs", "kots", "kds", "ready"]);
  if (!tab) return <div className="cap-msg">{err || "Loading order…"}</div>;
  const items = (tab.items || []).filter((i) => i.status !== "cancelled");
  const total = items.reduce((s, i) => s + Number(i.qty) * Number(i.unit_price || 0), 0);
  return (
    <div data-testid="cap-order-view">
      <ul className="cap-menu">
        {items.map((i) => (
          <li key={i.item_id || i.name} data-testid={`cap-order-item-${i.item_id}`}>
            <div><b>{i.qty} × {i.name}</b><small>{money(Number(i.qty) * Number(i.unit_price || 0))}{i.kot_no ? ` · ${i.kot_no}` : ""}</small></div>
            <span className={`cap-pill ${i.status}`}>{ITEM_LABEL[i.status] || i.status}</span>
          </li>
        ))}
        {!items.length && <li className="cap-empty">Nothing ordered yet.</li>}
      </ul>
      <div className="cap-order-total"><span>Running total</span><b data-testid="cap-order-total">{money(total)}</b></div>
      <button className="cap-add-more" onClick={onAdd} data-testid="cap-add-more"><Plus size={18} /> Add more items</button>
    </div>
  );
}

function MenuPicker({ recipes, cart, setQty }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const cats = [...new Set(recipes.map((r) => r.category || "Other"))];
  const list = recipes.filter((r) => (!cat || (r.category || "Other") === cat) && (!q || r.name.toLowerCase().includes(q.toLowerCase())));
  return (
    <>
      <div className="cap-search"><Search size={16} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes" data-testid="cap-search" /></div>
      <div className="cap-cats"><button className={!cat ? "on" : ""} onClick={() => setCat("")} data-testid="cap-cat-all">All</button>{cats.map((c) => <button key={c} className={cat === c ? "on" : ""} onClick={() => setCat(c)} data-testid={`cap-cat-${c}`}>{c}</button>)}</div>
      <ul className="cap-menu">
        {list.map((r) => {
          const n = cart[r.id] || 0;
          return (
            <li key={r.id} data-testid={`cap-item-${r.id}`}>
              <div><b>{r.name}</b><small>{money(r.selling_price)}</small></div>
              {n ? <div className="cap-qty"><button onClick={() => setQty(r.id, n - 1)} data-testid={`cap-minus-${r.id}`}><Minus size={16} /></button><span>{n}</span><button onClick={() => setQty(r.id, n + 1)} data-testid={`cap-plus-${r.id}`}><Plus size={16} /></button></div>
                : <button className="cap-add" onClick={() => setQty(r.id, 1)} data-testid={`cap-add-${r.id}`}>Add</button>}
            </li>
          );
        })}
        {!list.length && <li className="cap-empty">No dishes match.</li>}
      </ul>
    </>
  );
}

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.25].forEach((t) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.frequency.value = 880; o.connect(g); g.connect(ctx.destination); g.gain.setValueAtTime(0.25, ctx.currentTime + t); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.18); });
    navigator.vibrate?.([200, 100, 200]);
  } catch { /* sound blocked */ }
}

function ReadyAlerts({ branch }) {
  const [alerts, setAlerts] = useState([]);
  const known = useRef(new Set());
  const load = () => api.get(`/captain/ready-alerts?branch=${branch}`).then(({ data }) => {
    if (data.some((a) => !known.current.has(a.id)) && known.current.size + data.length > 0) beep();
    data.forEach((a) => known.current.add(a.id)); setAlerts(data);
  }).catch(() => {});
  useEffect(() => { if (branch) load(); }, [branch]); // eslint-disable-line
  useLive(() => load(), ["ready", "kds"]);
  const ack = (id) => { setAlerts((x) => x.filter((a) => a.id !== id)); api.post(`/captain/ready-alerts/${id}/ack`).catch(() => {}); };
  if (!alerts.length) return null;
  return (
    <div className="cap-ready" role="alert" data-testid="cap-ready-alerts">
      <div className="cap-ready-head"><BellRing size={18} /> Food ready · {alerts.length}</div>
      {alerts.map((a) => (
        <div key={a.id} className="cap-ready-row" data-testid={`cap-ready-${a.id}`}>
          <div><b>{/^t/i.test(a.table_label || "") ? a.table_label : `Table ${a.table_label || "—"}`} · {a.kot_no}</b><small>{a.items}</small></div>
          <button onClick={() => ack(a.id)} data-testid={`cap-ready-ack-${a.id}`}><Check size={16} /> Got it</button>
        </div>
      ))}
    </div>
  );
}

export default function CaptainMode({ branch, recipes, user, onFullView, logout }) {
  const [tables, setTables] = useState([]);
  const [table, setTable] = useState(null);
  const [adding, setAdding] = useState(false);
  const [cart, setCart] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const menu = useMemo(() => recipes.filter((r) => Number(r.selling_price) > 0 && r.available !== false), [recipes]);
  const load = () => api.get(`/tables?branch=${branch}`).then(({ data }) => setTables(data)).catch(() => {});
  useEffect(() => { if (branch) load(); }, [branch]); // eslint-disable-line
  useLive(() => load(), ["tables", "tabs", "kots", "kds", "sales"]);
  const setQty = (id, n) => setCart((c) => { const x = { ...c }; if (n > 0) x[id] = n; else delete x[id]; return x; });
  const count = Object.values(cart).reduce((a, b) => a + b, 0);
  const total = Object.entries(cart).reduce((s, [id, n]) => s + n * Number(menu.find((r) => r.id === id)?.selling_price || 0), 0);
  const pick = (t) => { setMsg(""); setErr(""); setTable(t); setAdding(!t.current_tab_id); };
  const back = () => { if (adding && table?.current_tab_id) { setAdding(false); setCart({}); } else { setTable(null); setCart({}); } };
  const send = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const tabId = table.current_tab_id || (await api.post(`/tables/${table.id}/open`)).data.id;
      for (const [id, n] of Object.entries(cart)) {
        const r = menu.find((x) => x.id === id);
        await api.post(`/tabs/${tabId}/items`, { recipe_id: r.id, name: r.name, qty: n, unit_price: r.selling_price, gst_rate: r.gst_rate ?? 5, hsn_sac: r.hsn_sac || "2106" });
      }
      const { data: kot } = await api.post(`/tabs/${tabId}/send-kot`);
      setMsg(`${kot.kot_no} sent to the kitchen for ${table.name || table.number}`); setCart({}); setTable(null); load();
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  const title = table ? `Table ${table.name || table.number}${adding ? "" : " · order"}` : "Pick a table";
  return (
    <div className="cap-shell" data-testid="captain-mode">
      <header className="cap-top">
        {table ? <button className="cap-icon" onClick={back} data-testid="cap-back"><ArrowLeft size={20} /></button> : <span className="cap-hello">Hi, {user?.name?.split(" ")[0] || "Captain"}</span>}
        <b data-testid="cap-title">{title}</b>
        <span className="cap-top-actions">
          {table?.current_tab_id && adding && <button className="cap-icon" onClick={() => setAdding(false)} title="Current order" data-testid="cap-view-order"><ClipboardList size={18} /></button>}
          <button className="cap-icon" onClick={onFullView} title="Full view" data-testid="cap-full-view"><Maximize2 size={18} /></button>
          <button className="cap-icon" onClick={logout} title="Sign out" data-testid="cap-logout"><LogOut size={18} /></button>
        </span>
      </header>
      <ReadyAlerts branch={branch} />
      {msg && <div className="cap-msg ok" data-testid="cap-message">{msg}</div>}
      {err && <div className="cap-msg err" data-testid="cap-error">{err}</div>}
      <main className="cap-body">
        {!table ? <TablePicker tables={tables} busy={busy} onPick={pick} />
          : adding ? <MenuPicker recipes={menu} cart={cart} setQty={setQty} />
          : <OrderView tabId={table.current_tab_id} onAdd={() => setAdding(true)} />}
      </main>
      {table && adding && count > 0 && <footer className="cap-bar"><div><b data-testid="cap-count">{count} item{count > 1 ? "s" : ""}</b><small>{money(total)}</small></div><button disabled={busy} onClick={send} data-testid="cap-send-kot">{busy ? <Loader2 className="spin" size={18} /> : <Send size={18} />} Send KOT</button></footer>}
    </div>
  );
}
